import type { ChatMessageContent } from "schemas/generated/chatMessage";

import { useCallback } from "react";

import { useChatTurn } from "@v2/features/chat/hooks/useChatTurn";
import { useCurrentChat } from "@v2/features/chat/hooks/useCurrentChat";
import { useChatStoreActions } from "@v2/features/chat/store/ChatStoreContext";
import { chatRouter, chatUtils } from "@v2/trpc/routes/chat";

/**
 * Helper hook to create a function that updates both the database and local store
 * for chat messages in sync. Invalidating getPaginatedMessages does not completly work because
 * our getPaginatedMessages call in ReactEditorChat only fetches the last 20 messages. So if the
 * updated message is not in the last 20 messages, it will not be updated.
 *
 * @author Ben (2025-11-03)
 */
export function useUpdateMessageInDBAndStore() {
  const { chatId } = useCurrentChat();
  const { addOrUpdateMessageForChat } = useChatStoreActions();

  const { getChatTurnId } = useChatTurn();

  const { mutate: updateMessage } = chatRouter.updateMessage.useMutation({
    onSuccess: () => {
      if (!chatId) {
        return;
      }

      void chatUtils.get.invalidate({ chatId });
      void chatUtils.getPaginatedMessages.invalidate({ chatId });
    },
  });

  return useCallback(
    (messageId: string, content: ChatMessageContent) => {
      if (!chatId) {
        return;
      }

      const chatTurnId = getChatTurnId();

      updateMessage({
        id: messageId,
        chatId,
        content,
      });
      addOrUpdateMessageForChat(chatId, messageId, content, "tool", chatTurnId);
    },
    [chatId, updateMessage, addOrUpdateMessageForChat, getChatTurnId],
  );
}
